import {useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import {htmlStringToReactElement} from '@s-ui/js/lib/react'
import {atomIconSizes} from '@s-ui/react-atom-icon'
import Context from '@s-ui/react-context'
import MoleculeModal from '@adv-mt/mt-modal-basic'
import ModalBasicContent from '@adv-mt/mt-modal-basic-content'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close.js'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {SLIDES_LENGTH} from '../config.js'
import {CtaFootnoteRapidPayLogalty, CtaFootnoteSantander, CtaFootnoteSantanderDGT} from './cta-foot-note.js'
import {ModalStepsContent} from './modal-steps-content.js'

const noop = () => {}

const getFootnote = (slide, isSeller) => {
  switch (slide) {
    case 1:
      return <CtaFootnoteSantanderDGT isFooterCentered={isSeller} />
    case 2:
      return <CtaFootnoteRapidPayLogalty isFooterCentered={isSeller} />
    case 0:
    case 3:
    default:
      return <CtaFootnoteSantander isFooterCentered={isSeller} />
  }
}

const ModalSteps = ({
  adId = '',
  isSeller = false,
  onCtaClick = noop,
  onModalClose = noop,
  setShowRejectRequestModal = noop
}) => {
  const {domain, i18n} = useContext(Context)
  const {culture} = i18n
  const {page, track} = useAnalyticsTrack()

  const {TRACKING_EVENT_NAMES, TRACKING_TRANSLATIONS_BY_CULTURE} = domain.get('config').get('tracking')
  const TRACKING_LITERALS = TRACKING_TRANSLATIONS_BY_CULTURE[culture]

  const [currentSlide, setCurrentSlide] = useState(0)
  const handleOnSlideChange = slide => setCurrentSlide(slide)

  useEffect(() => {
    ;(async () => {
      const pageName = isSeller
        ? `TRANSACTION_SELLER_ONBOARDING_STEP_${currentSlide + 1}`
        : `TRANSACTION_BUYER_ONBOARDING_STEP_${currentSlide + 1}`

      const trackingData = await domain.get('get_page_tracking_data_use_case').execute({
        page: pageName
      })

      page(trackingData)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlide])

  const handleOnConfirm = () => {
    domain
      .get('get_event_tracking_data_use_case')
      .execute({
        event:
          TRACKING_EVENT_NAMES[
            isSeller
              ? 'SELLER_PAYCAR_SAFE_PURCHASE_CLICKED_TRANSACTION'
              : 'BUYER_PAYCAR_SAFE_PURCHASE_CLICKED_TRANSACTION'
          ],
        ad: {id: adId},
        pageName:
          TRACKING_LITERALS[
            isSeller
              ? `TRANSACTION_SELLER_ONBOARDING_STEP_${currentSlide + 1}_PAGE_NAME`
              : `TRANSACTION_BUYER_ONBOARDING_STEP_${currentSlide + 1}_PAGE_NAME`
          ]
      })
      .then(eventTrackingData => {
        track(eventTrackingData)
      })

    onCtaClick()
  }

  return (
    <>
      <MoleculeModal
        isOpen
        closeOnOutsideClick
        closeOnEscKeyDown
        iconClose={<IconClose size={atomIconSizes.medium} />}
        onClose={onModalClose}
        size="large"
      >
        <ModalBasicContent
          stepsContent={[
            {
              buttons: isSeller
                ? {
                    cancelButton: {
                      customCallBack: () => {
                        setShowRejectRequestModal(true)
                      },
                      design: 'outline',
                      isDisabled: currentSlide !== SLIDES_LENGTH - 1,
                      text: i18n.t('COMMON.REJECT'),
                      type: 'secondary'
                    },
                    confirmButton: {
                      color: 'primary',
                      customCallBack: handleOnConfirm,
                      isDisabled: currentSlide !== SLIDES_LENGTH - 1,
                      text: i18n.t('COMMON.ACCEPT')
                    }
                  }
                : {
                    confirmButton: {
                      isDisabled: currentSlide !== SLIDES_LENGTH - 1,
                      color: 'primary',
                      customCallBack: handleOnConfirm,
                      text: i18n.t('COMMON.ASK_FOR_PURCHASE')
                    }
                  },
              buttonsContent: (
                <div className="mt-ModalTransactionDiscover-buttonsContent">{getFootnote(currentSlide, isSeller)}</div>
              ),
              children: <ModalStepsContent isSeller={isSeller} onSlideChange={handleOnSlideChange} />,
              headerImage: null,
              forceInlineButtons: isSeller
            }
          ]}
        />
      </MoleculeModal>
    </>
  )
}

ModalSteps.displayName = 'ModalSteps'
ModalSteps.propTypes = {
  adId: PropTypes.string,
  isSeller: PropTypes.bool,
  onCtaClick: PropTypes.func,
  onModalClose: PropTypes.func,
  setShowRejectRequestModal: PropTypes.func
}

export default ModalSteps
